import { useRef, type KeyboardEvent, type TouchEvent } from 'react';
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react';
import ArrowRightIcon from '@/assets/icons/arrow-right.svg?react';
import PhotoThumbnailCard from './PhotoThumbnailCard';
import { IPhotoSectionPhoto } from './types';
import styles from './PhotoCaptureSection.module.scss';

interface IPhotoStripProps {
  photos: IPhotoSectionPhoto[];
  pageIndex: number;
  pageSize: number;
  placeholderIconClassName?: string;
  totalItems: number;
  onNextPage: () => void;
  onPreviousPage: () => void;
  /* eslint-disable no-unused-vars */
  onPhotoClick: (photo: IPhotoSectionPhoto) => void;
  /* eslint-enable no-unused-vars */
}

function PhotoStrip({ photos, pageIndex, pageSize, placeholderIconClassName = '', totalItems, onNextPage, onPreviousPage, onPhotoClick }: IPhotoStripProps) {
  const touchStartPoint = useRef<{ x: number; y: number } | null>(null);
  const hasPreviousPage = pageIndex > 0;
  const hasNextPage = (pageIndex + 1) * pageSize < totalItems;
  const showPagination = hasPreviousPage || hasNextPage;
  const reserveTitleSpace = photos.some((photo) => Boolean(photo.typeLabel));

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft') onPreviousPage();
    if (event.key === 'ArrowRight') onNextPage();
  };

  const handleTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    const touch = event.touches[0];
    touchStartPoint.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
    const touchStart = touchStartPoint.current;
    const touch = event.changedTouches[0];
    touchStartPoint.current = null;

    if (!touchStart || !touch) return;

    const deltaX = touch.clientX - touchStart.x;
    const deltaY = touch.clientY - touchStart.y;
    const isHorizontalSwipe = Math.abs(deltaX) > 50 && Math.abs(deltaX) > Math.abs(deltaY);

    if (!isHorizontalSwipe) return;
    if (deltaX > 0) onPreviousPage();
    if (deltaX < 0) onNextPage();
  };

  return (
    <div className={styles.photoStripWrapper}>
      {showPagination && (
        <button type="button" className={`${styles.pageButton} ${styles.previousPageButton}`} aria-label="View previous photos" onClick={onPreviousPage} disabled={!hasPreviousPage}>
          <ArrowLeftIcon aria-hidden="true" />
        </button>
      )}

      <div className={styles.photoGrid} aria-label="Club photos" tabIndex={0} onKeyDown={handleKeyDown} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        {photos.map((photo) => (
          <PhotoThumbnailCard key={photo.id} photo={photo} title={photo.typeLabel} placeholderIconClassName={placeholderIconClassName} reserveTitleSpace={reserveTitleSpace} onClick={onPhotoClick} />
        ))}
      </div>

      {showPagination && (
        <button type="button" className={`${styles.pageButton} ${styles.nextPageButton}`} aria-label="View next photos" onClick={onNextPage} disabled={!hasNextPage}>
          <ArrowRightIcon aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

export default PhotoStrip;
